import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Loading } from "@/components/LoadingState";
import { toast } from "sonner";
import { Info, Download, Users as UsersIcon, Sparkles, CalendarRange, MousePointerClick, CheckSquare, X } from "lucide-react";
import { api, API, AYLAR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LeaveRecordDialog } from "@/components/LeaveRecordDialog";

const CATEGORIES = [
  { key: "calisma",       label: "Çalışıldı",     bg: "bg-emerald-500",  text: "text-white",    softBg: "bg-emerald-100",  softText: "text-emerald-900" },
  { key: "ucretli_izin",  label: "Ücretli İzin",  bg: "bg-sky-500",      text: "text-white",    softBg: "bg-sky-100",      softText: "text-sky-900"     },
  { key: "ucretsiz_izin", label: "Ücretsiz İzin", bg: "bg-amber-500",    text: "text-white",    softBg: "bg-amber-100",    softText: "text-amber-900"   },
  { key: "raporlu",       label: "Raporlu",       bg: "bg-rose-500",     text: "text-white",    softBg: "bg-rose-100",     softText: "text-rose-900"    },
  { key: "haftasonu",     label: "Hafta Sonu",    bg: "bg-slate-400",    text: "text-white",    softBg: "bg-slate-200",    softText: "text-slate-700"   },
  { key: "tatil",         label: "Resmi Tatil",   bg: "bg-violet-500",   text: "text-white",    softBg: "bg-violet-100",   softText: "text-violet-900"  },
];
const CAT_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

const DAY_NAMES = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

export default function PuantajTab({ yil, ay, onChange, refreshKey }) {
  const { isAdmin } = useAuth();
  const [personnel, setPersonnel] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCat, setActiveCat] = useState("calisma");
  const [lock, setLock] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [yearlyOpen, setYearlyOpen] = useState(false);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [leaveDialog, setLeaveDialog] = useState(null);

  // Load personnel list
  useEffect(() => {
    api.get("/personnel").then((r) => {
      const active = (r.data || []).filter((p) => p.aktif !== false);
      setPersonnel(active);
      if (!selectedId && active.length > 0) setSelectedId(active[0].id);
    }).catch(() => toast.error("Personel yüklenemedi"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCalendar = useCallback(async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const [c, lk, hol] = await Promise.all([
        api.get(`/puantaj/calendar?personnel_id=${selectedId}&yil=${yil}&ay=${ay}`),
        api.get(`/payroll/lock-status?yil=${yil}&ay=${ay}`),
        api.get(`/puantaj/holidays?yil=${yil}&ay=${ay}`),
      ]);
      setCalendar(c.data);
      setLock(lk.data);
      setHolidays(hol.data.holidays || []);
    } catch {
      toast.error("Takvim yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [selectedId, yil, ay]);

  useEffect(() => { loadCalendar(); }, [loadCalendar, refreshKey]);

  const isReadOnly = !!(lock?.locked && !isAdmin);

  const setDay = async (gun, kategori) => {
    if (isReadOnly) {
      toast.error("Bu ay kilitli");
      return;
    }
    if (calendar?.leave_marks?.[String(gun)]) {
      const lid = calendar?.leave_ids?.[String(gun)];
      if (lid) setLeaveDialog(lid);
      return;
    }
    if (multiSelectMode) {
      // Toggle in selection (don't persist yet)
      setSelectedDays((prev) => {
        const next = new Set(prev);
        if (next.has(gun)) next.delete(gun); else next.add(gun);
        return next;
      });
      return;
    }
    // Optimistic update
    setCalendar((c) => ({
      ...c,
      days: { ...c.days, [String(gun)]: kategori },
    }));
    try {
      const r = await api.post("/puantaj/calendar/day", {
        personnel_id: selectedId, yil, ay, gun, kategori,
      });
      setCalendar((c) => ({ ...c, days: r.data.days, totals: r.data.totals }));
      onChange?.();
    } catch (e) {
      if (e?.response?.status === 423) {
        toast.error(e.response.data?.detail?.message || "Bordro kilitli");
      } else {
        toast.error("Kaydedilemedi");
      }
      loadCalendar();
    }
  };

  const toggleMultiSelect = () => {
    setMultiSelectMode((v) => !v);
    setSelectedDays(new Set());
  };

  const selectAllDays = () => {
    if (!calendar) return;
    const ndays = new Date(yil, ay, 0).getDate();
    setSelectedDays(new Set(Array.from({ length: ndays }, (_, i) => i + 1)));
  };

  const clearSelection = () => setSelectedDays(new Set());

  const applyBulkCategory = async (kategori) => {
    if (isReadOnly) { toast.error("Bu ay kilitli"); return; }
    if (selectedDays.size === 0) { toast.error("Önce gün seçin"); return; }
    const label = (CAT_MAP[kategori] || {}).label || kategori;
    if (!window.confirm(`${selectedDays.size} güne "${label}" uygulanacak. Onaylıyor musunuz?`)) return;
    setApplying(true);
    const daysPatch = {};
    selectedDays.forEach((g) => { daysPatch[String(g)] = kategori; });
    try {
      const r = await api.post("/puantaj/calendar/bulk", {
        personnel_id: selectedId, yil, ay, days: daysPatch,
      });
      setCalendar((c) => ({ ...c, days: r.data.days, totals: r.data.totals }));
      toast.success(`${selectedDays.size} gün "${label}" olarak güncellendi`);
      setSelectedDays(new Set());
      setMultiSelectMode(false);
      onChange?.();
    } catch (e) {
      if (e?.response?.status === 423) {
        toast.error(e.response.data?.detail?.message || "Bordro kilitli");
      } else {
        toast.error(e?.response?.data?.detail || "Toplu uygulama başarısız");
      }
      loadCalendar();
    } finally {
      setApplying(false);
    }
  };

  const monthDays = useMemo(() => {
    if (!calendar) return [];
    const nDays = new Date(yil, ay, 0).getDate();
    const firstWeekday = (new Date(yil, ay - 1, 1).getDay() + 6) % 7; // Mon=0
    const grid = [];
    for (let i = 0; i < firstWeekday; i++) grid.push(null);
    for (let d = 1; d <= nDays; d++) grid.push(d);
    return grid;
  }, [calendar, yil, ay]);

  const holidayByDay = useMemo(() => {
    const m = {};
    for (const h of holidays) m[h.gun] = h;
    return m;
  }, [holidays]);

  const downloadPdf = async (mode) => {
    if (!selectedId) return;
    setPdfBusy(true);
    try {
      const res = await api.get(
        `/puantaj/pdf?personnel_id=${selectedId}&yil=${yil}&ay=${ay}&mode=${mode}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      const name = selectedPersonnel?.sicil_no || selectedId;
      link.href = url;
      link.setAttribute("download", `puantaj_${name}_${yil}_${String(ay).padStart(2, "0")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF indirildi");
    } catch {
      toast.error("PDF oluşturulamadı");
    } finally {
      setPdfBusy(false);
    }
  };

  const downloadBulkXlsx = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(
        `/puantaj/export?yil=${yil}&ay=${ay}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `puantaj_toplu_${yil}_${String(ay).padStart(2, "0")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Toplu Excel indirildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Excel üretilemedi");
    } finally {
      setPdfBusy(false);
    }
  };

  const selectedPersonnel = personnel.find((p) => p.id === selectedId);

  return (
    <div className="space-y-4">
      <div className="bg-slate-50 border border-slate-300 rounded-xl p-3 md:p-4 flex items-start gap-3">
        <Info size={16} className="text-slate-900 mt-0.5 shrink-0" />
        <div className="text-xs md:text-sm text-teal-900 flex-1">
          <strong className="font-semibold">Puantaj Takvimi:</strong>{" "}
          <span>Üstteki kategoriyi seçin, günlere tıklayın. Her tıklamada bordro otomatik güncellenir (ücretli izin, ücretsiz izin, raporlu gün sayıları).</span>
        </div>
      </div>

      {/* Personnel selector + toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 md:p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <UsersIcon size={18} className="text-slate-500" />
          <span className="text-sm font-bold text-slate-900">{AYLAR[ay-1].n} {yil}</span>
        </div>
        <div className="flex-1 min-w-[200px]">
          <select
            value={selectedId || ""}
            onChange={(e) => setSelectedId(e.target.value)}
            data-testid="puantaj-personnel-select"
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-semibold focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
          >
            {personnel.map((p) => (
              <option key={p.id} value={p.id}>{p.ad_soyad} · {p.pozisyon || "-"} · {p.sicil_no}</option>
            ))}
          </select>
        </div>
        {isReadOnly && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md bg-rose-100 text-rose-900">
            Kilitli (sadece okunur)
          </span>
        )}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => downloadPdf("full")}
            disabled={pdfBusy || !selectedId}
            data-testid="puantaj-pdf-full"
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
          >
            <Download size={13} /> Tam PDF
          </button>
          <button
            onClick={() => downloadPdf("simple")}
            disabled={pdfBusy || !selectedId}
            data-testid="puantaj-pdf-simple"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50"
          >
            <Download size={13} /> Sade PDF
          </button>
          <button
            onClick={downloadBulkXlsx}
            disabled={pdfBusy || personnel.length === 0}
            data-testid="puantaj-bulk-xlsx"
            title="Tüm aktif personel · her biri ayrı sheet · tek Excel"
            className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
          >
            <Download size={13} /> Toplu Excel
          </button>
          {isAdmin && (
            <button
              onClick={() => setBulkOpen(true)}
              disabled={isReadOnly}
              data-testid="puantaj-bulk-open"
              className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
            >
              <Sparkles size={13} /> Toplu Uygula
            </button>
          )}
          <button
            onClick={() => setYearlyOpen(true)}
            disabled={!selectedId}
            data-testid="puantaj-yearly-open"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50"
          >
            <CalendarRange size={13} /> Yıllık Görünüm
          </button>
        </div>
      </div>

      {/* Category picker (active brush) */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 md:p-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            {multiSelectMode ? "Çoklu seçim modu — tıkladığınız günler seçilir" : "Aktif kategori (günlere tıklayınca bu uygulanır)"}
          </div>
          {!isReadOnly && (
            <button
              onClick={toggleMultiSelect}
              data-testid="puantaj-multi-toggle"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                multiSelectMode
                  ? "bg-indigo-600 text-white border-transparent"
                  : "bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50"
              }`}
            >
              {multiSelectMode ? <><X size={13} /> Modu Kapat</> : <><MousePointerClick size={13} /> Çoklu Seçim</>}
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setActiveCat(c.key)}
              data-testid={`puantaj-cat-${c.key}`}
              disabled={multiSelectMode}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                activeCat === c.key
                  ? `${c.bg} ${c.text} border-transparent ring-2 ring-offset-1 ring-slate-400`
                  : `bg-white text-slate-700 border-slate-300 hover:bg-slate-50`
              }`}
            >
              <span className={`w-3 h-3 rounded-full ${c.bg}`}></span>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Calendar grid */}
      {loading && (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400">
          Takvim yükleniyor...
        </div>
      )}
      {!loading && calendar && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 md:p-5">
          <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2">
            {DAY_NAMES.map((n) => (
              <div key={n} className="text-center text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-wider py-1">
                {n}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1 md:gap-2" data-testid="puantaj-grid">
            {monthDays.map((d, i) => {
              if (d === null) return <div key={`e-${i}`} />;
              const cat = calendar.days[String(d)] || "calisma";
              const def = CAT_MAP[cat] || CAT_MAP.calisma;
              const hol = holidayByDay[d];
              const isSelected = multiSelectMode && selectedDays.has(d);
              return (
                <button
                  key={d}
                  disabled={isReadOnly}
                  onClick={() => setDay(d, activeCat)}
                  data-testid={`puantaj-day-${d}`}
                  title={hol ? `${d} · ${def.label} · ${hol.isim}` : `${d} · ${def.label}`}
                  className={`relative aspect-square rounded-md md:rounded-lg flex flex-col items-center justify-center text-xs md:text-sm font-bold transition-transform hover:scale-[1.03] active:scale-95 disabled:cursor-not-allowed disabled:opacity-70 ${def.bg} ${def.text} ${isSelected ? "ring-4 ring-indigo-500 ring-offset-1 scale-[1.02] shadow-lg z-10" : ""}`}
                >
                  <span className="text-base md:text-lg">{d}</span>
                  <span className="text-[8px] md:text-[10px] font-semibold opacity-90 line-clamp-1 px-1" data-testid={calendar.leave_marks?.[String(d)] ? `puantaj-leave-${d}` : undefined}>
                    {calendar.leave_marks?.[String(d)] === "yillik" ? "Yıllık izin" : def.label}
                  </span>
                  {hol && (
                    <span
                      className="absolute top-0.5 right-0.5 bg-white/95 text-violet-700 text-[8px] font-bold px-1 py-0.5 rounded"
                      title={hol.isim}
                    >
                      {hol.tip === "dini" ? "D" : hol.tip === "yarim" ? "½" : "M"}
                    </span>
                  )}
                  {cat === "calisma" && (calendar.yarim_weekdays || []).includes((new Date(yil, ay - 1, d).getDay() + 6) % 7) && (
                    <span className="absolute bottom-0.5 right-0.5 bg-teal-600 text-white text-[10px] md:text-xs font-black px-1.5 py-0.5 rounded shadow" title="Yarım gün (tam gün sayılır, tam yemek)" data-testid={`puantaj-yarim-${d}`}>½</span>
                  )}
                  {isSelected && (
                    <span className="absolute top-0.5 left-0.5 bg-indigo-600 text-white rounded-full w-4 h-4 grid place-items-center">
                      <CheckSquare size={10} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Multi-select floating action bar */}
          {multiSelectMode && (
            <div className="mt-4 bg-indigo-50 border border-indigo-300 rounded-lg p-3 flex items-center gap-2 flex-wrap sticky bottom-2" data-testid="puantaj-multi-bar">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-indigo-900 mr-2">
                <CheckSquare size={14} className="text-indigo-600" />
                {selectedDays.size} gün seçili
              </div>
              <button
                onClick={selectAllDays}
                data-testid="puantaj-select-all"
                className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 underline"
              >
                Tüm ayı seç
              </button>
              {selectedDays.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="text-xs font-semibold text-slate-600 hover:text-slate-900 underline"
                >
                  Seçimi temizle
                </button>
              )}
              <div className="ml-auto flex items-center gap-1 flex-wrap">
                <span className="text-[11px] font-bold text-indigo-700 uppercase tracking-wider mr-1">Uygula:</span>
                {CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    disabled={applying || selectedDays.size === 0}
                    onClick={() => applyBulkCategory(c.key)}
                    data-testid={`puantaj-apply-${c.key}`}
                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold ${c.bg} ${c.text} disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110`}
                    title={`Seçili günlere "${c.label}" uygula`}
                  >
                    <span className="w-2 h-2 rounded-full bg-white/70"></span>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Totals */}
      {calendar && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="puantaj-totals">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            {selectedPersonnel?.ad_soyad} · Aylık Özet
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {CATEGORIES.map((c) => (
              <div key={c.key} className={`rounded-lg p-3 ${c.softBg}`}>
                <div className={`text-[10px] font-bold uppercase tracking-wider ${c.softText}`}>{c.label}</div>
                <div className={`text-2xl font-bold mt-1 ${c.softText}`} data-testid={`puantaj-total-${c.key}`}>
                  {calendar.totals?.[c.key] || 0}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-slate-500">
            Bordroya yansıyanlar: <strong>Ücretli İzin</strong>, <strong>Ücretsiz İzin</strong>, <strong>Raporlu</strong>. Hafta sonu ve resmi tatil zaten aylık brüt içinde.
          </div>
        </div>
      )}

      {bulkOpen && (
        <BulkAssignModal
          yil={yil}
          ay={ay}
          personnel={personnel}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); loadCalendar(); onChange?.(); }}
        />
      )}

      {leaveDialog && (
        <LeaveRecordDialog
          leaveId={leaveDialog}
          personnelId={selectedId}
          onClose={() => setLeaveDialog(null)}
          onChanged={() => { loadCalendar(); onChange?.(); }}
        />
      )}

      {yearlyOpen && (
        <YearlyModal
          personnelId={selectedId}
          personnel={selectedPersonnel}
          yil={yil}
          onClose={() => setYearlyOpen(false)}
        />
      )}
    </div>
  );
}

function YearlyModal({ personnelId, personnel, yil, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/puantaj/yearly?personnel_id=${personnelId}&yil=${yil}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Yıllık görünüm yüklenemedi"))
      .finally(() => setLoading(false));
  }, [personnelId, yil]);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="puantaj-yearly-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl p-5 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 pb-3">
          <div>
            <h3 className="text-base font-bold text-slate-900">Yıllık Puantaj · {yil}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{personnel?.ad_soyad} · {personnel?.sicil_no}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-sm font-semibold">✕ Kapat</button>
        </div>

        {loading && <Loading text="Puantaj yükleniyor..." />}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              {CATEGORIES.map((c) => (
                <div key={c.key} className={`rounded-lg p-2 ${c.softBg}`}>
                  <div className={`text-[10px] font-bold uppercase ${c.softText}`}>{c.label}</div>
                  <div className={`text-xl font-bold mt-0.5 ${c.softText}`}>{data.year_totals?.[c.key] || 0}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {data.months.map((m) => (
                <MiniMonth key={m.ay} yil={yil} ay={m.ay} days={m.days} totals={m.totals} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MiniMonth({ yil, ay, days, totals }) {
  const nDays = new Date(yil, ay, 0).getDate();
  const firstWeekday = (new Date(yil, ay - 1, 1).getDay() + 6) % 7;
  const grid = [];
  for (let i = 0; i < firstWeekday; i++) grid.push(null);
  for (let d = 1; d <= nDays; d++) grid.push(d);
  const colorMap = {
    calisma: "bg-emerald-500",
    ucretli_izin: "bg-sky-500",
    ucretsiz_izin: "bg-amber-500",
    raporlu: "bg-rose-500",
    haftasonu: "bg-slate-300",
    tatil: "bg-violet-500",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-2">
      <div className="text-xs font-bold text-slate-700 mb-1 flex items-center justify-between">
        <span>{AYLAR[ay-1].n}</span>
        <span className="text-[10px] font-semibold text-slate-500">
          Ç{totals.calisma} · Üİ{totals.ucretli_izin} · R{totals.raporlu}
        </span>
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {["P","S","Ç","P","C","C","P"].map((n, i) => (
          <div key={`h${i}`} className="text-center text-[8px] font-bold text-slate-400">{n}</div>
        ))}
        {grid.map((d, i) => {
          if (d === null) return <div key={`e${i}`} />;
          const cat = days[String(d)] || "calisma";
          return (
            <div
              key={d}
              title={`${d} ${AYLAR[ay-1].n}: ${CAT_MAP[cat]?.label || cat}`}
              className={`aspect-square rounded-sm ${colorMap[cat] || "bg-slate-200"} flex items-center justify-center`}
            >
              <span className="text-[8px] font-bold text-white opacity-90">{d}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BulkAssignModal({ yil, ay, personnel, onClose, onDone }) {
  const nDays = new Date(yil, ay, 0).getDate();
  const [kategori, setKategori] = useState("tatil");
  const [guns, setGuns] = useState([]);
  const [targetAll, setTargetAll] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [overwrite, setOverwrite] = useState(true);
  const [busy, setBusy] = useState(false);

  const toggleGun = (g) => setGuns((xs) => xs.includes(g) ? xs.filter((x) => x !== g) : [...xs, g]);
  const togglePerson = (id) => setSelectedIds((xs) => xs.includes(id) ? xs.filter((x) => x !== id) : [...xs, id]);
  const selectAllDays = () => setGuns(Array.from({ length: nDays }, (_, i) => i + 1));
  const clearDays = () => setGuns([]);

  const submit = async () => {
    if (guns.length === 0) return toast.error("En az bir gün seç");
    if (!targetAll && selectedIds.length === 0) return toast.error("En az bir personel seç");
    setBusy(true);
    try {
      const payload = {
        yil, ay,
        guns: guns.sort((a, b) => a - b),
        kategori,
        personnel_ids: targetAll ? null : selectedIds,
        overwrite_existing: overwrite,
      };
      const r = await api.post("/puantaj/calendar/bulk-assign", payload);
      toast.success(`${r.data.updated_people} personel güncellendi · ${r.data.skipped_people} atlandı`);
      onDone();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "Hata";
      toast.error(typeof msg === "string" ? msg : "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="puantaj-bulk-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div>
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Sparkles size={16} /> Toplu Puantaj Uygula
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Seçtiğin günleri hedef personellere tek seferde yaz. {AYLAR[ay-1].n} {yil}.
          </p>
        </div>

        <div>
          <div className="text-[11px] font-semibold uppercase text-slate-500 mb-1.5">Kategori</div>
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setKategori(c.key)}
                data-testid={`bulk-cat-${c.key}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border ${
                  kategori === c.key ? `${c.bg} ${c.text} border-transparent` : "bg-white text-slate-700 border-slate-300"
                }`}
              >
                <span className={`w-2.5 h-2.5 rounded-full ${c.bg}`}></span>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] font-semibold uppercase text-slate-500">
              Günler ({guns.length} seçili)
            </div>
            <div className="flex gap-1">
              <button type="button" onClick={selectAllDays} className="text-[11px] text-indigo-700 font-semibold hover:underline">Tümü</button>
              <span className="text-slate-300">·</span>
              <button type="button" onClick={clearDays} className="text-[11px] text-slate-600 font-semibold hover:underline">Temizle</button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="bulk-days-grid">
            {Array.from({ length: nDays }, (_, i) => i + 1).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => toggleGun(g)}
                data-testid={`bulk-day-${g}`}
                className={`py-1.5 rounded text-xs font-bold border ${
                  guns.includes(g)
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[11px] font-semibold uppercase text-slate-500 mb-1.5">Hedef</div>
          <div className="flex gap-1.5 mb-2">
            <button
              type="button"
              onClick={() => setTargetAll(true)}
              data-testid="bulk-target-all"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
                targetAll ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"
              }`}
            >Tüm aktif personel ({personnel.length})</button>
            <button
              type="button"
              onClick={() => setTargetAll(false)}
              data-testid="bulk-target-selected"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
                !targetAll ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"
              }`}
            >Seçili personel</button>
          </div>
          {!targetAll && (
            <div className="border border-slate-200 rounded-md max-h-40 overflow-y-auto p-2 space-y-1">
              {personnel.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-50 px-1 py-1 rounded">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(p.id)}
                    onChange={() => togglePerson(p.id)}
                    data-testid={`bulk-pers-${p.sicil_no}`}
                  />
                  <span className="font-semibold">{p.ad_soyad}</span>
                  <span className="text-slate-500">· {p.sicil_no}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="bulk-overwrite" />
          Mevcut değerleri de üzerine yaz (işaretlenmezse sadece calisma/haftasonu/tatil günler değiştirilir)
        </label>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded-md text-sm font-semibold hover:bg-slate-50"
          >İptal</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="bulk-submit"
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md text-sm font-semibold"
          >{busy ? "Uygulanıyor..." : "Uygula"}</button>
        </div>
      </div>
    </div>
  );
}
